// Das Konto: Anzeige, Registrierung, Anmeldung.
//
// Der Ausgangszustand ist ein **Gastkonto**: ein Token im Browserspeicher, an
// dem der ganze Spielstand haengt. Das ist der schnellste Einstieg, den es
// gibt — und der leiseste Verlust: privater Modus zu, Speicher geleert,
// anderes Geraet, und der Fortschritt ist weg, ohne dass je etwas kaputtging.
//
// Deshalb gibt es hier zweierlei: einen **Weg**, das Konto zu sichern, und
// einen **Hinweis**, dass man das tun sollte. Beides ist freiwillig — Online
// ist keine Pflicht (`GAME-001`), und ein Konto erst recht nicht.
//
// Diese Schicht ist DOM (`ui`) und redet mit `net`. Zur Szene fuehrt kein
// Import, nur der Ereignisbus (Gate FG-04).

import { bus } from '../core/bus';
import { escapeHtml } from '../core/format';
import type { Session } from '../net/session';
import { hintSeen, markHintSeen } from '../state/store';
import type { Panels } from './panels';

/** Der Hinweis, den ein Gast einmal bekommt — und danach nie wieder. */
export const ACCOUNT_HINT = 'account';

const USERNAME_MIN = 3;
const PASSWORT_MIN = 8;

/**
 * Die Eingaben pruefen, bevor sie ans Netz gehen.
 *
 * Absichtlich **milder** als der Server: er entscheidet, das hier spart nur
 * den Weg. Was der Server zusaetzlich ablehnt (zu haeufiges Passwort, Name
 * vergeben), kommt als Meldung zurueck und wird angezeigt.
 */
export function validateRegistration(
  username: string,
  password: string,
  repeat: string,
): string | null {
  if (username.trim().length < USERNAME_MIN) {
    return `Der Benutzername braucht mindestens ${USERNAME_MIN} Zeichen.`;
  }
  if (password.length < PASSWORT_MIN) {
    return `Das Passwort braucht mindestens ${PASSWORT_MIN} Zeichen.`;
  }
  if (password !== repeat) return 'Die beiden Passwörter stimmen nicht überein.';
  return null;
}

export class Account {
  private readonly chip: HTMLElement;
  private letzterStand = '';

  constructor(
    private readonly session: Session,
    private readonly panels: Panels,
  ) {
    const chip = document.getElementById('account-chip');
    if (!chip) throw new Error('Panel-Element fehlt: account-chip');
    this.chip = chip;
    this.chip.onclick = () => this.open();
  }

  /** Einmal je Frame — wie das uebrige HUD, und genauso mit Aenderungsschluessel. */
  render(): void {
    const stand = `${this.session.mode}|${this.session.player?.guest ?? true}|${this.session.playerName}`;
    if (stand === this.letzterStand) return;
    this.letzterStand = stand;

    if (this.session.mode === 'solo') {
      this.chip.className = '';
      this.chip.textContent = '○ Solo — kein Konto';
      return;
    }
    if (this.session.isGuest) {
      this.chip.className = 'gast';
      this.chip.textContent = '⚠ Gast — Konto sichern';
      return;
    }
    this.chip.className = 'fest';
    this.chip.innerHTML = `✓ ${escapeHtml(this.session.playerName)}`;
  }

  /** Der Hinweis, sobald der Spieler etwas zu verlieren hat. */
  hintIfGuest(): void {
    if (!this.session.isGuest || hintSeen(ACCOUNT_HINT)) return;
    markHintSeen(ACCOUNT_HINT);
    this.panels.showModal('KONTO SICHERN?',
      '<p>Du spielst als Gast. Dein Spielstand hängt an <b>diesem Browser</b> — ' +
      'wird der Speicher geleert, ist er weg, und auf einem anderen Gerät gibt es ihn nicht.</p>' +
      '<p style="margin-top:12px;opacity:.85">Ein Konto dauert zehn Sekunden und behält alles, ' +
      'was du bisher abgebaut hast.</p>',
      [
        { label: 'Jetzt registrieren', fn: () => this.open() },
        { label: 'Später', dim: true },
      ]);
  }

  /** Den passenden Dialog zeigen — Solo, Gast oder angemeldet. */
  open(): void {
    if (this.session.mode === 'solo') {
      this.panels.showModal('KEIN KONTO MÖGLICH',
        '<p>Du spielst gerade solo — der Server ist nicht erreichbar. Dein Spielstand liegt ' +
        'in diesem Browser.</p>' +
        '<p style="margin-top:12px;opacity:.85">Sobald die Verbindung steht, kannst du dir hier ' +
        'ein Konto anlegen.</p>',
        [{ label: 'Weiter graben' }]);
      return;
    }
    if (this.session.isGuest) this.showRegister();
    else this.showAccount();
  }

  private showRegister(fehler = ''): void {
    this.panels.showModal('KONTO ANLEGEN',
      '<p>Dein Fortschritt bleibt vollständig erhalten — das Konto wird auf den ' +
      'laufenden Spielstand gesetzt, nicht daneben.</p>'
      + feld('konto-user', 'Benutzername', 'text', 'username')
      + feld('konto-pw', 'Passwort', 'password', 'new-password')
      + feld('konto-pw2', 'Passwort wiederholen', 'password', 'new-password')
      + `<div class="form-error">${escapeHtml(fehler)}</div>`
      + '<div class="form-note">Keine E-Mail, keine Bestätigung. Merk dir das Passwort — '
      + 'es gibt keinen Weg, es zurückzusetzen.</div>',
      [
        {
          label: 'Konto anlegen',
          submit: (body) => this.readRegister(body),
          fn: () => void this.doRegister(),
        },
        { label: 'Ich habe schon ein Konto', dim: true, fn: () => this.showLogin() },
      ]);
    focusFirst();
  }

  private showLogin(fehler = ''): void {
    this.panels.showModal('ANMELDEN',
      '<p>Melde dich an, um an deinem Spielstand weiterzuspielen.</p>'
      + feld('konto-user', 'Benutzername', 'text', 'username')
      + feld('konto-pw', 'Passwort', 'password', 'current-password')
      + `<div class="form-error">${escapeHtml(fehler)}</div>`
      + '<div class="form-note">Achtung: es gilt dann der Spielstand des Kontos. Was du gerade '
      + 'als Gast gespielt hast, wird <b>nicht</b> übernommen.</div>',
      [
        {
          label: 'Anmelden',
          submit: (body) => this.readLogin(body),
          fn: () => void this.doLogin(),
        },
        { label: 'Zurück', dim: true, fn: () => this.showRegister() },
      ]);
    focusFirst();
  }

  private showAccount(): void {
    this.panels.showModal('DEIN KONTO',
      `<p>Angemeldet als <b>${escapeHtml(this.session.playerName)}</b>.</p>`
      + '<p style="margin-top:12px;opacity:.85">Dein Spielstand liegt auf dem Server. Du kannst '
      + 'dich auf jedem Gerät anmelden und dort weiterspielen.</p>',
      [
        { label: 'Weiter graben' },
        { label: 'Abmelden', dim: true, fn: () => void this.doLogout() },
      ]);
  }

  // ── Eingaben ───────────────────────────────────────────────────────────────
  private eingabe = { user: '', pw: '', pw2: '' };

  private readRegister(body: HTMLElement): void {
    this.eingabe = {
      user: wert(body, 'konto-user'),
      pw: wert(body, 'konto-pw'),
      pw2: wert(body, 'konto-pw2'),
    };
  }

  private readLogin(body: HTMLElement): void {
    this.eingabe = { user: wert(body, 'konto-user'), pw: wert(body, 'konto-pw'), pw2: '' };
  }

  // ── Zuege ──────────────────────────────────────────────────────────────────
  private async doRegister(): Promise<void> {
    const { user, pw, pw2 } = this.eingabe;
    const fehler = validateRegistration(user, pw, pw2);
    if (fehler) { this.showRegister(fehler); return; }

    const result = await this.session.register(user.trim(), pw);
    if (!result.ok) { this.showRegister(result.message ?? ''); return; }

    markHintSeen(ACCOUNT_HINT);
    bus.emit('toast', { text: `Konto angelegt — willkommen, ${this.session.playerName}`, tone: 'gold' });
  }

  private async doLogin(): Promise<void> {
    const { user, pw } = this.eingabe;
    if (!user.trim() || !pw) { this.showLogin('Bitte Benutzername und Passwort eingeben.'); return; }

    const result = await this.session.login(user.trim(), pw);
    if (!result.ok) { this.showLogin(result.message ?? ''); return; }

    markHintSeen(ACCOUNT_HINT);
    bus.emit('account:changed', undefined);
    bus.emit('state:changed', undefined);
    bus.emit('toast', { text: `Angemeldet als ${this.session.playerName}`, tone: 'good' });
  }

  private async doLogout(): Promise<void> {
    const result = await this.session.logout();
    if (!result.ok) {
      bus.emit('toast', { text: result.message ?? 'Fehlgeschlagen', tone: '' });
      return;
    }
    bus.emit('account:changed', undefined);
    bus.emit('state:changed', undefined);
    bus.emit('toast', { text: 'Abgemeldet — du spielst wieder als Gast', tone: '' });
  }
}

function feld(id: string, label: string, type: string, autocomplete: string): string {
  return `<div class="form-row"><label for="${id}">${label}</label>
    <input id="${id}" type="${type}" autocomplete="${autocomplete}" autocapitalize="off"
      autocorrect="off" spellcheck="false"></div>`;
}

function wert(body: HTMLElement, id: string): string {
  const input = body.querySelector<HTMLInputElement>('#' + id);
  return input?.value ?? '';
}

function focusFirst(): void {
  document.querySelector<HTMLInputElement>('#modal-body input')?.focus();
}
